import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import {
  AlertService,
  AuthService,
  LocationService,
  MixpanelService,
  SharedService,
} from '@kodit/core/services';
import {
  CreateIzlaznaFakturaCommand, GetIzlaznaFakturaQuery, IEOtpremnicaDropdownDto,
  IIzlaznaFakturaDto, ILpfrLicencaDropdownDto,
  IOpstaPravilaKonfiguracijaDto, IVoziloDropdownDto, IzlazneFaktureClient, RacuniClient, SubscriptionPackage, TipDokumentaOtpremnica, TipRacuna,
  UpdateIzlaznaFakturaCommand,
  VrstaFakture
} from '@kodit/core/data-api';
import { ConfirmationService } from 'primeng/api';
import { RacunService } from '../../../../../../services/src/lib/racun.service';
import {
  FormHelper,
  IzlaznaFakturaFormService,
  OpstaPravilaFormService, StavkaFormService, VoziloFormService
} from 'libs/core/form-definitions/src';
import { ActivatedRoute } from '@angular/router';
import { RacunFormConfig } from '../../subforms/racun-form-container/racun-form.config';
import { FormBuilderTypeSafe, FormGroupTypeSafe } from 'angular-typesafe-reactive-forms-helper';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BreadcrumbService } from 'xng-breadcrumb';
import { IzlaznaFakturaService } from '../izlazna-faktura.service';
import {DynamicDialogConfig, DynamicDialogRef, DynamicDialogService} from "@kodit/core/shared-ui";
import {HasAvansnaUplataDialogComponent} from "../has-avansna-uplata-dialog/has-avansna-uplata-dialog.component";
import {DialogSize} from "../../../../../../shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config";
import { finalize, map } from 'rxjs/operators';

@Component({
  selector: 'kodit-izlazna-faktura-form',
  templateUrl: './izlazna-faktura-form.component.html',
  styleUrls: ['./izlazna-faktura-form.component.scss'],
})
export class IzlaznaFakturaFormComponent implements OnInit, OnDestroy {
  /** Subs */
  private _subs: Subscription = new Subscription();

  /** Props */
  isLoading = false;
  readonly skeletonRows = Array(3).fill(null);
  readonly skeletonFields = Array(5).fill(null);
  fakturaForm: FormGroupTypeSafe<IIzlaznaFakturaDto>;
  racunFormConfig: RacunFormConfig;
  opstaPravilaForm: FormGroupTypeSafe<IOpstaPravilaKonfiguracijaDto>;
  tipOtpremnica: TipDokumentaOtpremnica = TipDokumentaOtpremnica.OTPREMNICA;
  tipPrijemnica: TipDokumentaOtpremnica = TipDokumentaOtpremnica.PRIJEMNICA;
  hasEOtpremnice: boolean;

  /** Configuration */
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(
    DialogSize.SMALL
  );

  constructor(
    private _client: IzlazneFaktureClient,
    private _racuniClient: RacuniClient,
    private _alertService: AlertService,
    private _confirmationService: ConfirmationService,
    private _locationService: LocationService,
    private _racunService: RacunService,
    private _formService: IzlaznaFakturaFormService,
    private _route: ActivatedRoute,
    private _formHelper: FormHelper,
    private _breadcrumbService: BreadcrumbService,
    private _service: IzlaznaFakturaService,
    private _opstaPravilaService: OpstaPravilaFormService,
    private _mixpanelService: MixpanelService,
    private _sharedService: SharedService,
    private _voziloService : VoziloFormService,
    private _dialogService: DynamicDialogService,
    private _fbs: FormBuilderTypeSafe,
    private _stavkaService: StavkaFormService,
    private _authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.hasEOtpremnice = this._authService.hasPackage(
      SubscriptionPackage[SubscriptionPackage.EOTPREMNICE]
    );
    this.isLoading = true;
    const editId = Number.parseInt(this._route.snapshot.paramMap.get('id'));
    const formConfig$ = this._racuniClient.getFormConfiguration().pipe(map((x) => x.data));

    if (editId) {
      this._subs.add(
        forkJoin([
          formConfig$,
          this._client.get(new GetIzlaznaFakturaQuery({ id: editId })).pipe(map((x) => x.data)),
        ])
          .pipe(finalize(() => { this.isLoading = false; }))
          .subscribe(([formConfig, racun]) => {
            this.initializeForm(formConfig, racun);
          })
      );
    } else {
      this._subs.add(
        formConfig$
          .pipe(finalize(() => { this.isLoading = false; }))
          .subscribe((formConfig) => {
            const racunFromRoute = this._route.snapshot.data?.racun;
            this.initializeForm(formConfig, racunFromRoute);
          })
      );
    }
  }

  get jeEditMode(): boolean {
    return !!this.fakturaForm?.controls.racunDto?.value?.id;
  }

  private initializeForm(formConfig: any, racun?: any): void {
    this.fakturaForm = this._formService.GetIzlaznaFakturaFormGroup(
      formConfig,
      racun
    );
    this.opstaPravilaForm = this._opstaPravilaService.GetOpstaPravilaFormGroup(
      formConfig
    );

    if (this._route.snapshot.data.racunForPrijemnica !== undefined) {
      const ePrijemniceData = this._route.snapshot.data.racunForPrijemnica.ePrijemnice;

      this.fakturaForm.setControl(
        'ePrijemnice',
        this._formService.GetEOtpremniceDropdownFormArray(ePrijemniceData)
      );
      this.fakturaForm.controls.ePrijemnice.updateValueAndValidity();

      const stavkeData = this._route.snapshot.data.racunForPrijemnica.stavkeDto;
      const racunGroup = this.fakturaForm.controls.racunDto as FormGroup;
      racunGroup.setControl(
        'stavkeDto',
        this._stavkaService.GetFormArray(stavkeData)
      );

      const stavkeArray = this.fakturaForm.controls.racunDto.get('stavkeDto') as FormArray;

      stavkeArray.controls.forEach((stavka: FormGroup) => {
        const razlogControl = stavka.get('razlogPdvIzuzecaId');

        razlogControl?.setValidators(Validators.nullValidator);
        razlogControl?.updateValueAndValidity();
      });

      this.fakturaForm.controls.racunDto.get('strankaRacunaDto').patchValue(this._route.snapshot.data.racunForPrijemnica.strankaRacunaDto);
      this.fakturaForm.controls.racunDto.get('strankaRacunaDto').updateValueAndValidity();

      this._racunService.calculateUkupno();
    }

    this._service.setForm = this.fakturaForm;

    this._racunService.setBrojRacunaLabel = 'Broj fakture';
    this.racunFormConfig = {
      tip: TipRacuna.IZLAZNA_FAKTURA,
      racunForm: this.fakturaForm.controls.racunDto,
      brojRacunaTooltip: 'Dodeljen broj fakture',
      originalanBrojRacunaTooltip:
        'Unesite broj fakture koji ste dobili od izdavaoca',
      saveCallback: () => this.onSave(),
      cancelCallback: () => this.onCancel(),
    };

    this._subs.add(
      this._racunService.getJeDomaciRacun.subscribe((val) => {
        this.fakturaForm.controls.vrstaFakture.patchValue(
          val ? VrstaFakture.DOMACA : VrstaFakture.STRANA
        );
      })
    );

    if (!!this.fakturaForm.controls.avansnaUplata?.value.avansniRacunId) {
      this.openDialog();
    }

    this._setBreadcrumb();
  }

  onSave() {
    this._formHelper.removeEmptyStavka(
      this.fakturaForm.controls.racunDto.get('stavkeDto') as FormArray
    );

    // check is it valid
    if (!this.fakturaForm.valid) {
      console.log(this.fakturaForm);
      this._formHelper.invalidateForm(this.fakturaForm);
      return;
    }

    const stavkeArray = this.fakturaForm.controls.racunDto.get(
      'stavkeDto'
    ) as FormArray;
    const imaStavkuBezOznakePdvKategorije = stavkeArray.controls.some(
      (stavka) =>
        stavka.value.stopaPDV === 0 && stavka.value.pdvKategorija == null
    );
    if (imaStavkuBezOznakePdvKategorije) {
      this._alertService.addWarnMsg(
        'Za stavke sa PDV stopom 0% morate izabrati oznaku PDV kategorije.'
      );
      return;
    }

    const imaStavkuBezRazlogaPdvIzuzeca = stavkeArray.controls.some(
      (stavka) =>
        stavka.value.stopaPDV === 0 && stavka.value.razlogPdvIzuzecaId == null
    );
    if (imaStavkuBezRazlogaPdvIzuzeca) {
      this._alertService.addWarnMsg(
        'Za stavke sa PDV stopom 0% morate izabrati razlog PDV izuzeća.'
      );
      return;
    }

    if (this._racunService.proveraIdentifikatora()) {
      this._alertService.addWarnMsg("Račun ne sme sadržati istovremeno stavke koje se odnose i koje se ne odnose na promet nekretnina");
      return;
    }

    this._mixpanelService.izlaznaFakturaSubmitted(this.fakturaForm.value);

    //add informacija isporuke if informacijaIsporukeId == null
    this.fakturaForm.value.informacijaIsporuke.strankaId = this.fakturaForm.controls.racunDto.value.strankaRacunaDto.id;

    // everything is ok, so let's save it
    if (this.jeEditMode) {
      this._sharedService.displayLoadingScreen(
        true,
        'Ažuriranje izlazne fakture...'
      );
      // it's edit
      this._subs.add(
        this._client
          .update(
            this.fakturaForm.value.racunDto.id,
            this.fakturaForm.value as UpdateIzlaznaFakturaCommand
          )
          .pipe(
            finalize(() => {
              this._sharedService.resetLoadingScreen();
            })
          )
          .subscribe({
            next: () => {
              this._mixpanelService.izlaznaFakturaSucceeded();
              this._alertService.addSuccessMsg(
                `Faktura ${this.fakturaForm.value.racunDto.broj} je uspešno izmenjena.`
              );
              this._locationService.routeBack();
            },
            error: () => {
              // Uneti podaci ostaju na formi — korisnik moze da pokusa ponovo.
              this._alertService.addFailedMsg(
                'Greška pri ažuriranju fakture. Uneti podaci su sačuvani, pokušajte ponovo.'
              );
            },
          })
      );
    } else {
      this._sharedService.displayLoadingScreen(
        true,
        'Kreiranje izlazne fakture...'
      );
      // it's new
      this._subs.add(
        this._client
          .create(this.fakturaForm.value as CreateIzlaznaFakturaCommand)
          .pipe(
            finalize(() => {
              this._sharedService.resetLoadingScreen();
            })
          )
          .subscribe({
            next: () => {
              this._mixpanelService.izlaznaFakturaSucceeded();
              this._alertService.addSuccessMsg(`Faktura je uspešno kreirana.`);
              if (
                !this.opstaPravilaForm.controls.kreirajOtpremnicu.value ||
                this.fakturaForm.value.racunDto.vezaniRacuniDto.length !== 0
              ) {
                this._locationService.routeBack();
                return;
              }
              this._alertService.addSuccessMsg(`Otpremnica je uspešno kreirana.`);
              this._locationService.routeBack();
            },
            error: () => {
              // Uneti podaci ostaju na formi — korisnik moze da pokusa ponovo.
              this._alertService.addFailedMsg(
                'Greška pri kreiranju fakture. Uneti podaci su sačuvani, pokušajte ponovo.'
              );
            },
          })
      );
    }
  }

  onCancel() {
    this._confirmationService.confirm({
      message: 'Ukoliko otkažete sav ne snimljen sadržaj će biti izgubljen.',
      acceptLabel: 'Ipak otkaži',
      rejectLabel: 'Ostani na stranici',
      header: 'Potvrdite otkazivanje',
      icon: 'far fa-check-circle',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this._locationService.routeBack();
      },
    });
  }

  selectedPrijemnice(selectedItems: IEOtpremnicaDropdownDto[]){
    (this.fakturaForm.controls.ePrijemnice as FormArray).clear();
    selectedItems.forEach((res : IEOtpremnicaDropdownDto) => {
      (this.fakturaForm.controls.ePrijemnice as FormArray).push(
        this._formService.GetEOtpremnicaDropdownFormGroup(res)
      );
    });
  }

  selectedOtpremnice(selectedItems: IEOtpremnicaDropdownDto[]){
    (this.fakturaForm.controls.eOtpremnice as FormArray).clear();
    selectedItems.forEach((res : IEOtpremnicaDropdownDto) => {
      (this.fakturaForm.controls.eOtpremnice as FormArray).push(
        this._formService.GetEOtpremnicaDropdownFormGroup(res)
      );
    });
  }


  selectedVozila(selectedItems: any){
    (this.fakturaForm.controls.vozila as FormArray).clear();
    selectedItems.forEach((res : IVoziloDropdownDto) => {
      (this.fakturaForm.controls.vozila as FormArray).push(
        this._voziloService.GetVoziloDropdownFormGroup(res)
      );
    });
  }

  openDialog() {
    this.dialogConfig.data = {
      brojAvansnog : this.fakturaForm.controls.avansnaUplata.value.avansniRacunBroj,
      datumIzdavanja : this.fakturaForm.controls.avansnaUplata.value.avansniRacunDatumIzdavanja
    };
    this.dialogConfig.header = 'Vezana avansna uplata za račun';
    this.dialogConfig.hideSubmit = true;
    this.dialogConfig.closable = false;
    this.dialogConfig.customCancelButton = { label : 'U redu', icon : '' };
    const ref: DynamicDialogRef = this._dialogService.open(
      HasAvansnaUplataDialogComponent,
      this.dialogConfig
    );

    this._subs.add(
      ref.onClose.subscribe(() => {
        const avansneArray = [this.fakturaForm.controls.avansnaUplata.value];
        this._racunService.setAvansneUplateRacuna = avansneArray;
        this._racunService.setOdabraneAvansneUplateZaRacunNonModified = avansneArray
      })
    );
  }

  private _setBreadcrumb() {
    this._breadcrumbService.set(
      '@unosIzlazneFaktureZaStranku',
      `Unos izlazne fakture za '${this.fakturaForm.value.racunDto.strankaRacunaDto.naziv}'`
    );
    this._breadcrumbService.set(
      '@izmenaIzlazneFakture',
      `Izmena izlazne fakture ${this.fakturaForm.value.racunDto.broj}`
    );
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
    this._service.resetService();
  }

  protected readonly TipDokumentaOtpremnica = TipDokumentaOtpremnica;
}
